import IconButton from '@material-ui/core/IconButton';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TextField from '@material-ui/core/TextField';
import { default as DeleteIcon } from '@material-ui/icons/Close';
import { nanoid } from 'nanoid';
import React, { useCallback, useEffect, useRef, useState } from 'react';

interface Props {
  defaultParameters: any[];
  search?: string;
  hideKmsKeyArn?: boolean;
  classes?: any;
  onParametersChange: (parameters: any) => void;
}

interface RowProps {
  param: any;
  hideKmsKeyArn?: boolean;
  isLast: boolean;
  onEditParameter: (prevParam: any, newParam: any) => void;
  onParameterInputBlur: (param: any) => void;
  onRemoveParameter: (param: any) => void;
};

const ParamTableRow = React.memo((props: RowProps) => {
  const { param, hideKmsKeyArn, isLast, onEditParameter, onParameterInputBlur, onRemoveParameter } = props;

  const onTextFieldChange = (event) => {
    onEditParameter(param, { ...param, [event.target.name]: event.target.value });
  };

  return (<TableRow>
    <TableCell>
      <TextField
        fullWidth
        name="name"
        placeholder="name"
        autoComplete="off"
        defaultValue={param.name}
        onChange={onTextFieldChange}
        InputProps={{
          onBlur: () => onParameterInputBlur(param),
          spellCheck: false
        }}
      />
    </TableCell>
    <TableCell>
      <TextField
        fullWidth
        name="value"
        placeholder="value"
        autoComplete="off"
        defaultValue={param.value}
        multiline
        rowsMax={10}
        onChange={onTextFieldChange}
        InputProps={{
          onBlur: () => onParameterInputBlur(param),
          spellCheck: false
        }}
      />
    </TableCell>
    {!hideKmsKeyArn && <TableCell>
      <TextField
        fullWidth
        name="kmsKeyArn"
        placeholder="key arn"
        autoComplete="off"
        defaultValue={param.kmsKeyArn}
        multiline
        rowsMax={10}
        onChange={onTextFieldChange}
        InputProps={{
          onBlur: () => onParameterInputBlur(param),
          spellCheck: false
        }}
      />
    </TableCell>}
    <TableCell align="right">
      {!isLast && <IconButton onClick={() => onRemoveParameter(param)}><DeleteIcon /></IconButton>}
    </TableCell>
  </TableRow>);
});

function FaroParameterListInput(props: Props) {
  const { defaultParameters, search, hideKmsKeyArn, onParametersChange } = props;
  const [parameters, setParameters] = useState([
    ...defaultParameters.map((param) => ({ name: param.Name, value: param.Value, kmsKeyArn: param.KmsKeyArn, id: nanoid() })),
    { name: '', value: '', kmsKeyArn: '', id: nanoid() }
  ]);
  const didMount = useRef(false);

  useEffect(() => {
    if (didMount.current) {
      onParametersChange(parameters.filter(({ name, value, kmsKeyArn }) => name !== '' || value !== '' || kmsKeyArn !== '')
        .map(({ name, value, kmsKeyArn }) => ({ Name: name, Value: value, KmsKeyArn: kmsKeyArn })));
    } else {
      didMount.current = true;
    }
  }, [parameters, onParametersChange]);

  const onEditParameter = useCallback((prevParameter, newParameter) => {
    setParameters(prevParameters => {
      const parametersCopy = prevParameters.slice();
      const index = parametersCopy.indexOf(prevParameter);
      parametersCopy[index] = newParameter;
      if (index === (prevParameters.length - 1) && !prevParameter.name && !prevParameter.value && !prevParameter.kmsKeyArn) {
        // Add new empty param to end of list
        parametersCopy.push({ name: '', value: '', kmsKeyArn: '', id: nanoid() });
      }
      return parametersCopy;
    });
  }, []);

  const onRemoveParameter = useCallback((parameter) => {
    setParameters(prevParameters => {
      const parametersCopy = prevParameters.slice();
      parametersCopy.splice(parametersCopy.indexOf(parameter), 1);
      return parametersCopy;
    });
  }, []);

  const onParameterInputBlur = useCallback((parameter) => {
    setParameters(prevParameters => {
      const parametersCopy = prevParameters.slice();
      const index = parametersCopy.indexOf(parameter);
      if (index !== (prevParameters.length - 1) && !parameter.name && !parameter.value && !parameter.kmsKeyArn) {
        parametersCopy.splice(index, 1);
        return parametersCopy;
      } else {
        return prevParameters;
      }
    });
  }, []);

  const normalizedSearch = search ? search.toLowerCase() : '';
  const filteredParameters = normalizedSearch ? parameters.filter(({ name, value }) => {
    return name.toLowerCase().includes(normalizedSearch) || value.toLowerCase().includes(normalizedSearch);
  }) : parameters;

  return (
    <TableContainer>
      <Table size="small">
        <TableHead className={props.classes ? props.classes.tableHead : null}>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Value</TableCell>
            {!hideKmsKeyArn && <TableCell>KMS Key ARN (optional)</TableCell>}
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredParameters.map((param, index) => <ParamTableRow
            key={param.id}
            param={param}
            isLast={index === (filteredParameters.length - 1) && !search}
            hideKmsKeyArn={hideKmsKeyArn}
            onEditParameter={onEditParameter}
            onParameterInputBlur={onParameterInputBlur}
            onRemoveParameter={onRemoveParameter}
          />)}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default FaroParameterListInput;
